import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { sellerApi, getToken, clearSession, isReady } from '../sellerAuth';
import { useOfflineQueue } from '../pos/useOfflineQueue';
import MerchantShell from '../pos/MerchantShell';
import ChargeScreen from '../pos/ChargeScreen';
import DashboardScreen from '../pos/DashboardScreen';
import ActivityScreen from '../pos/ActivityScreen';
import ProfileScreen from '../pos/ProfileScreen';

const POSApp = () => {
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [feeRate, setFeeRate] = useState(0.0149);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('charge');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await sellerApi.get('/seller/me');
      setSeller(res.data);
      return res.data;
    } catch (e) { return null; }
  }, []);

  const { online } = useOfflineQueue();

  useEffect(() => {
    (async () => {
      if (!getToken()) { navigate('/seller'); return; }
      try {
        const res = await sellerApi.get('/seller/me');
        if (!isReady(res.data)) { navigate('/activate'); return; }
        setSeller(res.data);
      } catch (e) { clearSession(); navigate('/seller'); return; }
      try {
        const f = await sellerApi.get('/settings/fee');
        if (typeof f.data?.fee_rate === 'number') setFeeRate(f.data.fee_rate);
      } catch (e) { /* default */ }
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, []);

  const logout = () => { clearSession(); navigate('/seller'); };

  const onCard = async ({ amount, tax, tip }) => {
    if (amount < 1) { toast.error('Minimum sale is $1.00'); return; }
    setBusy(true);
    try {
      const res = await sellerApi.post('/payments/checkout', { seller_id: seller.id, amount, tax, tip, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start payment');
      setBusy(false);
    }
  };

  if (loading || !seller) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 size={40} className="text-primary animate-spin" /></div>;
  }

  return (
    <>
      {!online && (
        <div className="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-md z-50 bg-destructive text-destructive-foreground text-xs font-bold py-2 px-4 flex items-center justify-center gap-2">
          <WifiOff size={14} strokeWidth={2.75} /> Offline · card charges paused
        </div>
      )}
      <MerchantShell active={tab} onChange={setTab}>
        {tab === 'charge' && <ChargeScreen seller={seller} feeRate={feeRate} online={online} busy={busy} onCard={onCard} />}
        {tab === 'dashboard' && <DashboardScreen seller={seller} />}
        {tab === 'activity' && <ActivityScreen transactions={seller.transactions || []} onReload={reload} />}
        {tab === 'profile' && <ProfileScreen seller={seller} onSignOut={logout} onUpdated={setSeller} />}
      </MerchantShell>
    </>
  );
};

export default POSApp;
